// Ambient types for '@rails/actioncable'. Customer sites compile this package's
// source directly, and their tsconfig doesn't include the package's .d.ts files,
// so this reference is required for the import below to resolve in consumer builds.
// eslint-disable-next-line @typescript-eslint/triple-slash-reference
/// <reference path="../../types/rails-actioncable.d.ts" />
'use client';

import { useCallback, useEffect, useRef } from 'react';
import { createConsumer } from '@rails/actioncable';
import { error as logError, log, warn } from '../../tracking/logging';

export interface RealtimeSubscriptionData {
  token: string;
  contact_id: number;
  cable_url: string;
}

interface UseRealtimeReplyOrchestratorOptions {
  debugLabel: string;
  fetchRealtimeData: () => Promise<RealtimeSubscriptionData | null>;
  loadLatestHasReply: () => Promise<boolean>;
  onReplyResolved: () => void;
  onAgentThinking?: () => void;
}

export function useRealtimeReplyOrchestrator({
  debugLabel,
  fetchRealtimeData,
  loadLatestHasReply,
  onReplyResolved,
  onAgentThinking,
}: UseRealtimeReplyOrchestratorOptions) {
  const cableRef = useRef<ReturnType<typeof createConsumer> | null>(null);
  const subscriptionRef = useRef<{ unsubscribe: () => void } | null>(null);
  const subscribedContactRef = useRef<number | null>(null);

  const clearRealtime = useCallback(() => {
    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe();
      subscriptionRef.current = null;
    }
    if (cableRef.current) {
      cableRef.current.disconnect();
      cableRef.current = null;
    }
    subscribedContactRef.current = null;
  }, []);

  const resolveReply = useCallback(() => {
    onReplyResolved();
  }, [onReplyResolved]);

  const subscribeRealtime = useCallback((realtime: RealtimeSubscriptionData) => {
    const token = realtime.token;
    const contactIdForStream = realtime.contact_id;
    const cableUrl = realtime.cable_url;
    if (!token || !contactIdForStream || !cableUrl) return;

    if (
      subscribedContactRef.current === contactIdForStream &&
      subscriptionRef.current &&
      cableRef.current
    ) {
      return;
    }

    clearRealtime();

    const cable = createConsumer(`${cableUrl}?token=${encodeURIComponent(token)}`);
    cableRef.current = cable;
    subscribedContactRef.current = contactIdForStream;
    subscriptionRef.current = cable.subscriptions.create(
      { channel: 'ContactCommunicationsChannel', contact_id: String(contactIdForStream) },
      {
        connected: () => {
          log('chat-realtime', 'REALTIME_CONNECTED', {
            debugLabel,
            contactId: contactIdForStream,
          }, { shipToPostHog: true });
        },
        disconnected: () => {
          warn('chat-realtime', 'REALTIME_DISCONNECTED', {
            debugLabel,
            contactId: contactIdForStream,
          });
        },
        rejected: () => {
          warn('chat-realtime', 'REALTIME_REJECTED', {
            debugLabel,
            contactId: contactIdForStream,
          });
        },
        received: async (data: { type?: string }) => {
          log('chat-realtime', 'REALTIME_RECEIVED', {
            debugLabel,
            type: data?.type ?? 'unknown',
            contactId: contactIdForStream,
          }, { shipToPostHog: true });
          if (data?.type === 'agent_thinking') {
            onAgentThinking?.();
            return;
          }
          if (data?.type !== 'new_communication') return;
          try {
            const hasReply = await loadLatestHasReply();
            if (hasReply) {
              resolveReply();
            }
          } catch (error) {
            logError('chat-realtime', 'REALTIME_RECEIVE_HANDLER_ERROR', {
              debugLabel,
              contactId: contactIdForStream,
              message: error instanceof Error ? error.message : String(error),
              stack: error instanceof Error ? error.stack || '' : '',
            });
          }
        },
      }
    );
  }, [clearRealtime, debugLabel, loadLatestHasReply, onAgentThinking, resolveReply]);

  const ensureRealtimeSubscription = useCallback(async () => {
    try {
      const realtime = await fetchRealtimeData();
      if (!realtime) return;
      subscribeRealtime(realtime);
    } catch {
      // Realtime is best-effort; UI remains in waiting state until connection succeeds.
    }
  }, [fetchRealtimeData, subscribeRealtime]);

  const beginReplyWait = useCallback((options?: { realtimeData?: RealtimeSubscriptionData | null }) => {
    if (options?.realtimeData) {
      subscribeRealtime(options.realtimeData);
    } else {
      void ensureRealtimeSubscription();
    }
  }, [ensureRealtimeSubscription, subscribeRealtime]);

  useEffect(() => {
    return () => {
      clearRealtime();
    };
  }, [clearRealtime]);

  return {
    ensureRealtimeSubscription,
    subscribeRealtime,
    beginReplyWait,
  };
}
